import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { entriesApi } from '../../../../api/entries';
import { projectsApi } from '../../../../api/projects';
import { useCurrentUser } from '../../../../contexts/CurrentUserContext';
import { TimeEntry, Project } from '../../../../types';
import { getWorkingDatesForWeek, toInputDate } from '../../../../lib/utils';
import {
  quickHoursToRangeFrom,
  rangeToHours,
  isHalfHourStep,
  HALF_HOUR_ERROR,
  sumHoursForDate,
  findOverlappingEntry,
  futureDateError,
  routesToDirectManager,
  DEFAULT_START_HOUR,
  MIN_DAILY_HOURS,
  OVERLAP_ERROR_MESSAGE,
} from '../../../../lib/timeEntryHelpers';
import { Button } from '../../../../components/ui/atoms/button';
import { Input } from '../../../../components/ui/atoms/input';
import { Textarea } from '../../../../components/ui/atoms/textarea';
import { DialogContent, DialogHeader, DialogTitle } from '../../../../components/ui/organisms/dialog';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '../../../../components/ui/organisms/form';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../../components/ui/molecules/select';
import { SearchableSelect } from '../../../../components/ui/molecules/searchable-select';
import { EditValues, EditInput } from '../interface';
import { FinalizeDayDialog } from './FinalizeDayDialog';
import {
  editSchema, INTERNAL_MEETING_ERROR, PERSONAL_TRAINING_ERROR, containsInternalMeeting, containsTraining,
  isEntryProjectFrozen, isEntryProjectClosed,
} from '../utils';
import { ModeToggle } from '../components';

// The training-confirmation prompt is rendered INSIDE this same DialogContent
// (swapped via showTrainingDialog) rather than as a second, separate <Dialog>.
// Two independent Radix Dialog roots stacked on top of each other is a known
// trap: a click inside the top one can get read as an "outside click" on the
// one underneath, since they don't share a dismissable-layer stack — which
// silently closed the edit dialog the moment "Personal Training" was
// clicked, even though nothing had been saved. Keeping it as one Dialog with
// two content states avoids that entirely.
export function EditEntryDialog({ entry, onClose }: { entry: TimeEntry; onClose: () => void }) {
  const queryClient = useQueryClient();

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showTrainingDialog, setShowTrainingDialog] = useState(false);
  const [showFinalizeDay, setShowFinalizeDay] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState<{ id: string; body: any } | null>(null);

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list().then((r) => r.data),
  });
  const projects: Project[] = projectsData ?? [];

  const { user: me } = useCurrentUser();
  const defaultJobTitleId = me?.jobTitleId ?? '';

  const editDate = toInputDate(entry.date);
  const workingDates = getWorkingDatesForWeek(editDate);
  const { data: dayEntriesData } = useQuery({
    queryKey: ['my-entries-for-day', editDate],
    queryFn: () =>
      entriesApi.listMine({ from: editDate, to: editDate, limit: 50 })
        .then((r) => (r.data?.data ?? []).filter((e) => e.status !== 'REJECTED')),
  });

  const dayRanges = useMemo(
    () =>
      (dayEntriesData ?? []).map((e: any) => ({
        id: e.id,
        date: e.date?.slice(0, 10) ?? '',
        fromHour: e.fromHour?.includes?.('T') ? e.fromHour.slice(11, 16) : e.fromHour,
        toHour: e.toHour?.includes?.('T') ? e.toHour.slice(11, 16) : e.toHour,
      })),
    [dayEntriesData],
  );

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) => entriesApi.update(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-entries'] });
      queryClient.invalidateQueries({ queryKey: ['my-calendar'] });
      queryClient.invalidateQueries({ queryKey: ['my-entries-for-day'] });
      onClose();
    },
  });

  const rawFrom = entry.fromHour;
  const rawTo = entry.toHour;
  const hasExplicitRange = !!rawFrom && !!rawTo;

  // Where this entry already sits in the day. Quick Hours changes how long it
  // runs, not when it starts.
  const startHour = (rawFrom?.includes('T') ? rawFrom.slice(11, 16) : rawFrom) || DEFAULT_START_HOUR;

  const form = useForm<EditInput, unknown, EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      mode: hasExplicitRange ? 'range' : 'quick',
      projectId: entry.projectId,
      phaseId: entry.phaseId,
      jobTitleId: entry.jobTitleId,
      date: toInputDate(entry.date),
      hours: entry.hours,
      fromHour: startHour,
      toHour: rawTo?.includes('T') ? rawTo.slice(11, 16) : rawTo ?? '',
      notes: entry.notes,
    },
  });
  const watchedProjectId  = form.watch('projectId');
  const watchedMode       = form.watch('mode');
  const watchedHours      = form.watch('hours');
  const watchedFromHour   = form.watch('fromHour');
  const watchedToHour     = form.watch('toHour');

  const editPhases            = projects.find((p) => p.id === watchedProjectId)?.type?.phases ?? [];
  const editProjectIsBillable = !!projects.find((p) => p.id === watchedProjectId)?.billable;

  // ─── Live daily-hours warning (computed as the user types) ───────────────────

  const dailyWarning = useMemo(() => {
    if (!dayEntriesData) return null;

    const liveHours =
      watchedMode === 'quick'
        ? (Number(watchedHours) || 0)
        : (watchedFromHour && watchedToHour && watchedFromHour !== watchedToHour
            ? rangeToHours(watchedFromHour, watchedToHour)
            : 0);

    if (liveHours <= 0) return null;

    const total = sumHoursForDate(dayEntriesData, editDate, entry.id) + liveHours;
    if (Math.round(total * 100) === MIN_DAILY_HOURS * 100) return null;
    return `This day would total ${Math.round(total * 100) / 100}h; you'll be asked to finalize it to exactly ${MIN_DAILY_HOURS}h before saving.`;
  }, [editDate, dayEntriesData, watchedMode, watchedHours, watchedFromHour, watchedToHour, entry.id]);

  // ─── Submit: convert whichever mode was used into fromHour/toHour/hours ──────

  const onSubmit = (values: EditValues) => {
    setSubmitError(null);

    const targetProject = projects.find((p) => p.id === values.projectId);
    if (targetProject?.status === 'FROZEN' || isEntryProjectFrozen(entry, projects)) {
      setSubmitError('Cannot edit hours for a frozen project.');
      return;
    }
    if (targetProject?.status === 'CLOSED' || isEntryProjectClosed(entry, projects)) {
      setSubmitError('Cannot edit hours for a closed project.');
      return;
    }

    if (!workingDates.includes(values.date)) {
      setSubmitError('Entries can only be logged from Sunday through Thursday.');
      return;
    }

    let fromHour: string;
    let toHour: string;
    let hours: number;

    if (values.mode === 'quick') {
      const range = quickHoursToRangeFrom(values.hours!, startHour);
      fromHour = range.fromHour;
      toHour = range.toHour;
      hours = values.hours!;
    } else {
      fromHour = values.fromHour!;
      toHour = values.toHour!;
      hours = rangeToHours(fromHour, toHour);
    }

    if (!isHalfHourStep(hours)) {
      setSubmitError(HALF_HOUR_ERROR);
      return;
    }

    // Non-project time is the one place internal meetings, training and
    // learning legitimately belong; against a real project they're blocked,
    // in any spacing/punctuation variant.
    const isNonProjectRelated = routesToDirectManager(values.projectId);
    if (!isNonProjectRelated && containsInternalMeeting(values.notes)) {
      setSubmitError(INTERNAL_MEETING_ERROR);
      return;
    }


    // Future dates: unrestricted for non-project time, current week only otherwise.
    const futureError = futureDateError(values.date, isNonProjectRelated);
    if (futureError) {
      setSubmitError(futureError);
      return;
    }

    const durationChanged = hours !== entry.hours;
    if (!durationChanged && findOverlappingEntry(dayRanges, values.date, fromHour, toHour, entry.id)) {
      setSubmitError(OVERLAP_ERROR_MESSAGE);
      return;
    }

    const body = {
      projectId: values.projectId,
      phaseId: values.phaseId,
      jobTitleId: values.jobTitleId,
      date: values.date,
      fromHour,
      toHour,
      hours,
      notes: values.notes,
    };

    // If the notes mention "training" on a project entry, pause and ask
    // once before actually calling the API.
    if (!isNonProjectRelated && containsTraining(values.notes)) {
      setPendingUpdate({ id: entry.id, body });
      setShowTrainingDialog(true);
      return;
    }

    commit({ id: entry.id, body });
  };

  /**
   * Last gate before writing. The whole day goes to FinalizeDayDialog when it
   * wouldn't land on exactly 8h, and also whenever this entry's duration
   * changed — a longer or shorter entry shifts everything after it, so the day
   * has to be re-stacked and written as one piece. Only an edit that leaves
   * every entry's length alone (notes, project, a hand-picked time range) can
   * go straight out as a single update.
   */
  const commit = (update: { id: string; body: any }) => {
    const existingTotal = sumHoursForDate(dayEntriesData ?? [], update.body.date, entry.id);
    const dayIsComplete = Math.round((existingTotal + update.body.hours) * 100) === MIN_DAILY_HOURS * 100;
    if (!dayIsComplete || update.body.hours !== entry.hours) {
      setPendingUpdate(update);
      setShowFinalizeDay(true);
      return;
    }
    updateMutation.mutate(update);
  };

  const handleModelTrainingConfirm = () => {
    setShowTrainingDialog(false);
    const update = pendingUpdate;
    setPendingUpdate(null);
    if (update) commit(update);
  };

  const handlePersonalTrainingConfirm = () => {
    // Flips showTrainingDialog back to false, which swaps the *content* of
    // the still-open Dialog back to the edit form (values untouched) and
    // shows the blocking error below. The dialog itself never closes here —
    // nothing was saved, and the UI now makes that unambiguous.
    setShowTrainingDialog(false);
    setPendingUpdate(null);
    setSubmitError(PERSONAL_TRAINING_ERROR);
    // Bring focus back to Notes so the error is easy to spot and fix.
    form.setFocus('notes');
  };

  return (
    <DialogContent
      // While the training prompt is showing, block dismissal via
      // Escape/outside-click so a stray click can't silently drop the
      // in-progress edit.
      onEscapeKeyDown={(e: any) => (showTrainingDialog || showFinalizeDay) && e.preventDefault()}
      onPointerDownOutside={(e: any) => (showTrainingDialog || showFinalizeDay) && e.preventDefault()}
      onInteractOutside={(e: any) => (showTrainingDialog || showFinalizeDay) && e.preventDefault()}
    >
      {showFinalizeDay && pendingUpdate ? (
        <FinalizeDayDialog
          date={pendingUpdate.body.date}
          pending={pendingUpdate}
          projects={projects}
          defaultJobTitleId={defaultJobTitleId}
          onBack={() => { setShowFinalizeDay(false); setPendingUpdate(null); }}
          onSaved={onClose}
        />
      ) : showTrainingDialog ? (
        <>
          <DialogHeader>
            <DialogTitle>Confirm entry type</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This note mentions "training" on a project entry. Is this model training, or personal training?
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={handlePersonalTrainingConfirm}>Personal Training</Button>
            <Button onClick={handleModelTrainingConfirm}>Model Training</Button>
          </div>
        </>
      ) : (
        <>
          <DialogHeader>
            <DialogTitle>Edit Entry</DialogTitle>
          </DialogHeader>

          {/* Why the approver sent this back — shown so the employee can act on it without leaving the dialog. */}
          {entry.status === 'EDIT_REQUESTED' && entry.rejectionReason && (
            <div className="rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-900 px-3 py-2 text-sm text-purple-900 dark:text-purple-300">
              <span className="font-medium">Returned for edit:</span> {entry.rejectionReason}
            </div>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control} name="projectId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      Project
                      {editProjectIsBillable && (
                        <span className="text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded px-1.5 py-0.5">
                          Billable
                        </span>
                      )}
                    </FormLabel>
                    <FormControl>
                      <SearchableSelect
                        value={field.value}
                        onValueChange={(value) => { field.onChange(value); form.setValue('phaseId', ''); }}
                        options={projects
                          .filter((p) => p.status !== 'FROZEN' && p.status !== 'CLOSED')
                          .map((p) => ({ value: p.id, label: p.name }))}
                        placeholder="Select project"
                        searchPlaceholder="Search projects..."
                        className="h-auto min-h-10 text-left [&>span]:min-w-0 [&>span]:whitespace-normal [&>span]:[overflow-wrap:anywhere]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control} name="phaseId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phase</FormLabel>
                    {editPhases.length > 0 ? (
                      <Select onValueChange={field.onChange} value={field.value ?? ''}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Select a phase" /></SelectTrigger></FormControl>
                        <SelectContent>
                          {editPhases.map((ph) => <SelectItem key={ph.id} value={ph.id}>{ph.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm text-muted-foreground rounded-md border border-dashed px-3 py-2">
                        No phases configured for this project type.
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField control={form.control} name="date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl><SelectTrigger><SelectValue placeholder="Select date" /></SelectTrigger></FormControl>
                      <SelectContent>
                        {workingDates.map((date) => (
                          <SelectItem key={date} value={date}>
                            {new Date(`${date}T12:00:00Z`).toLocaleDateString('en-GB', {
                              weekday: 'long', day: 'numeric', month: 'short', timeZone: 'UTC',
                            })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* ── Quick Hours / From-To toggle ── */}
              <div>
                <FormLabel className="mb-2 block">Time</FormLabel>
                <ModeToggle mode={watchedMode} onChange={(m) => form.setValue('mode', m, { shouldDirty: true })} />

                {watchedMode === 'quick' ? (
                  <FormField control={form.control} name="hours"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input type="number" step="0.5" min="0.5" max="12" placeholder="e.g. 8" {...field} />
                        </FormControl>
                        <p className="text-xs text-muted-foreground mt-1">
                          Keeps its {startHour} start; end time calculated automatically.
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <FormField control={form.control} name="fromHour"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">From</FormLabel>
                          <FormControl><Input type="time" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField control={form.control} name="toHour"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">To</FormLabel>
                          <FormControl><Input type="time" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>

              <FormField control={form.control} name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl><Textarea placeholder="What did you work on?" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* ── 8h/day warning (live preview as you type; blocked for real on submit below) ── */}
              {dailyWarning && (
                <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 px-3 py-2 text-sm text-amber-800 dark:text-amber-400">
                  {dailyWarning}
                </div>
              )}

              {/* ── Overlap / note-guardrail error (blocking) ── */}
              {submitError && (
                <p className="text-sm text-destructive">{submitError}</p>
              )}

              {updateMutation.error && (
                <p className="text-sm text-destructive">
                  {(updateMutation.error as any)?.response?.data?.error || 'Failed to update'}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                <Button type="submit" disabled={updateMutation.isPending || !form.formState.isDirty}>
                  {updateMutation.isPending ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </form>
          </Form>
        </>
      )}
    </DialogContent>
  );
}
